import { useEffect } from "react";

/**
 * Sends the height of the document body to the parent window.
 * This function is used to dynamically resize an iframe containing this app.
 * The message sent includes the current scroll height of the document body
 * and an identifier for the iframe.
 */
function sendHeightToParent() {
  let height = document.body.scrollHeight;
  window.parent.postMessage(
    {
      frameHeight: height, // The current height of the document body
      frameId: "appIframeID", // Identifier for the iframe
    },
    "*"
  ); // Sends the message to any parent window (* is the wildcard for all origins)
}

export default function WindowResizer() {
  /**
   * useEffect #2: Sets up an interval to repeatedly send the current height of the document to the parent window.
   * The height is sent every 1000 milliseconds (1 second) to ensure the iframe is properly resized.
   * The interval is cleared when the component unmounts to avoid memory leaks.
   */
  useEffect(() => {
    const interval = setInterval(() => {
      sendHeightToParent(); // Send height to parent every second
    }, 1000);

    return () => clearInterval(interval); // Clear interval on component unmount
  }, []); // Runs only once on mount (empty dependency array)

  /**
   * useEffect #3: Sends the initial height of the document to the parent window on mount.
   * It also sets up an event listener to handle window resizing, ensuring that if the window
   * is resized, the new height is sent to the parent window to resize the iframe accordingly.
   * The event listener is removed on component unmount to avoid memory leaks.
   */
  useEffect(() => {
    sendHeightToParent(); // Send initial height on mount

    const handleResize = () => {
      sendHeightToParent(); // Send height on window resize
    };

    // Add event listener for window resize
    window.addEventListener("resize", handleResize);

    // Cleanup: remove event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []); // Runs only once on mount (empty dependency array)

  return null;
}
